import {
  Arrange,
  Button,
  Container,
  FormField,
  Media,
  Spacer,
  Text,
  TextInput,
} from '@loomhq/lens';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateLoginEmailError } from 'actions/login';
import { ERROR } from 'constants/error-severities';
import * as oAuthKeys from 'constants/oAuthKeys';
import { checkIfUserExists } from 'creators/login';
import { selectCurrentVideoId, selectPlayerTime } from 'selectors/videoPlayer';
import * as auth from 'utilities/auth';

import appleIconSrc from 'img/icons/apple-black.svg';
import googleIconSrc from 'img/icons/google.svg';
import slackIconSrc from 'img/icons/slack.svg';

import { afterOauthLogin } from '../utilities';
import { showErrorBar } from '../../../../actions/errorBar';
import { ENTER } from '../../../../constants/keyCodes';

import LegalAgreement from 'components/legal-agreement';

import styles from './styles.module.less';

const ACCOUNT_EXISTS_MSG = 'You have an account already! Sign in.';

const ShareSignUp = ({ email, emailError, setEmail, nextClickHandler }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);

  const videoId = useSelector(state => state.modals?.options?.videoId);
  const addReplyId = useSelector(state => state.modals?.options?.addReplyId);
  const videoCurrentTime = useSelector(selectPlayerTime);
  const repliedToVideoId = useSelector(selectCurrentVideoId);

  const connect = provider => {
    auth.openOAuthPopup(provider, {}, data => {
      if (data.loginSuccess) {
        afterOauthLogin({
          videoId,
          addReplyId,
          videoCurrentTime,
          repliedToVideoId,
          provider,
        });
      } else {
        dispatch(
          showErrorBar(
            ERROR,
            'There was a problem logging you in. Please try again later.'
          )
        );
      }
    });
  };

  const onEmailSignup = async () => {
    const userExists = await dispatch(checkIfUserExists(email));

    if (userExists) {
      dispatch(updateLoginEmailError(ACCOUNT_EXISTS_MSG));

      return;
    }

    nextClickHandler();
  };

  return (
    <Container paddingTop={2}>
      <Media
        queries={['(min-width: 400px)']}
        values={['large']}
        defaultValue="medium"
      >
        {buttonSize => (
          <Button
            logoSrc={googleIconSrc}
            hasFullWidth={true}
            size={buttonSize}
            onClick={() => connect(oAuthKeys.GOOGLE)}
          >
            Sign up with Google
          </Button>
        )}
      </Media>
      <details
        onToggle={event => setExpanded(event.target.hasAttribute('open'))}
      >
        <summary className={styles.signupShowMoreOptions}>
          <Spacer x="small" y="xsmall">
            <Text fontWeight="medium" alignment="center" color="bodyDimmed">
              Show {expanded ? 'less' : 'more'} options
            </Text>
          </Spacer>
        </summary>
        <Spacer top="medium" />
        <Arrange
          gap="small"
          columns={{ default: ['1fr'], xsmall: ['1fr', '1fr'] }}
        >
          <Button
            logoSrc={slackIconSrc}
            hasFullWidth={true}
            onClick={() => connect(oAuthKeys.SLACK)}
          >
            with Slack
          </Button>

          <Button
            logoSrc={appleIconSrc}
            hasFullWidth={true}
            onClick={() => connect(oAuthKeys.APPLE)}
          >
            with Apple
          </Button>
        </Arrange>

        <Spacer top={1.5} />
        <Container paddingY={3}>
          <div className={styles.line} />
        </Container>

        <FormField
          className="width:full"
          labelFor="email"
          errorMessage={emailError}
        >
          <Text size="small" fontWeight="medium">
            Or email address
          </Text>
          <Spacer top={1} bottom={1}>
            <TextInput
              id="email"
              placeholder="name@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              type="email"
              onKeyDown={e => {
                if (e.keyCode === ENTER) {
                  onEmailSignup();
                }
              }}
            />
          </Spacer>
          <Button
            size="medium"
            variant="primary"
            hasFullWidth={true}
            onClick={onEmailSignup}
          >
            Sign up
          </Button>
        </FormField>
      </details>
      <div className="text:center pt:large pb:small">
        <LegalAgreement size="small" />
      </div>
    </Container>
  );
};

export default ShareSignUp;
